/** @format */

import React, { FC, useRef, useState } from 'react'
import { MonitorCrumb } from 'src/components'
import { useHistory, useLocation } from 'react-router'
import { useAsync } from 'react-use'
import useNavRegion from 'src/hooks/useNavRegion'
import {
  _$,
  Card,
  Form,
  FormItem,
  Input,
  Select,
  Option,
  Tooltip,
  Icon,
  Button,
  Message
} from '@king-design/react'
import { getPromethuesInstance } from 'src/api/ApiPromethuesInstance'
import {
  getPrometheusAlertInhibitions,
  createPrometheusAlertInhibition,
  updatePrometheusAlertInhibition
} from 'src/api/ApiPrometheusAlarm'
import { DenoiseInhibitionEditProps, initDenoiseRule, initInhibitionFormValues } from '../constants'
import MatchRule from '../components/MatchRule'
import styled from 'styled-components'

function useQuery() {
  return new URLSearchParams(useLocation().search)
}
const DenoiseInhibitionEdit: FC<{ className?: string }> = ({ className }) => {
  const history = useHistory()
  const query = useQuery()
  const inhibitionID = query.get('inhibitionID')
  const copyID = query.get('copyID')
  // 控制台region
  const region = useNavRegion()
  // form表单
  const formRef = useRef(null)
  const [formValue, setFormValue] = useState<DenoiseInhibitionEditProps>(initInhibitionFormValues)
  // 账户下实例
  const { value: instances } = useAsync(async () => {
    if (region) {
      const result = await getPromethuesInstance({
        SearchRegion: region
      })
      return (
        result?.InstancesSet?.map(item => {
          return { name: item.InstanceName, id: item.ControllerID }
        }) || []
      )
    } else {
      return []
    }
  }, [region])

  useAsync(async () => {
    if ((copyID || inhibitionID) && instances !== undefined) {
      const result = await getPrometheusAlertInhibitions({
        ID: parseInt(copyID || inhibitionID)
      })
      const rule = result?.Rules?.[0]
      if (rule) {
        setFormValue({
          Name: copyID ? `${rule.Name}_copy` : rule.Name,
          InstanceIDs: rule.BindInstances.map(item => item.InstanceID) || [],
          Description: rule.Description || '',
          SourceParallelMatchers: rule.SourceParallelMatchers || [[initDenoiseRule]],
          TargetParallelMatchers: rule.TargetParallelMatchers || [[initDenoiseRule]],
          Equals: rule.Equals || []
        })
      }
    }
  }, [inhibitionID, copyID, instances])

  // 提交
  const formSubmit = async () => {
    const validate = await formRef?.current?.validate()
    try {
      if (validate) {
        const {
          Name,
          InstanceIDs,
          Description,
          SourceParallelMatchers,
          TargetParallelMatchers,
          Equals
        } = formValue
        const params = {
          Name,
          InstanceIDs,
          Description,
          SourceParallelMatchers,
          TargetParallelMatchers,
          Equals
        }
        const result = inhibitionID
          ? await updatePrometheusAlertInhibition({
              ID: parseInt(inhibitionID),
              ...params
            })
          : await createPrometheusAlertInhibition({
              ...params
            })
        if (result?.code === 'Success') {
          Message.success(_$('操作成功'))
          history.goBack()
        }
      }
    } catch (e) {
      return false
    }
  }

  return (
    <div className={className}>
      <MonitorCrumb
        breadArr={[
          { title: _$('Prometheus监控') },
          {
            title: _$('告警管理'),
            route: '/monitor/prometheusmonitor/alarm?tab=denoise&denoiseTab=inhibition'
          },
          { title: inhibitionID ? _$('编辑抑制规则') : _$('新建抑制规则') }
        ]}
      />
      <Card>
        <Form className="form_container" starOnRequired ref={formRef} labelWidth={100}>
          <FormItem
            label={_$('抑制规则名称')}
            value={formValue.Name}
            rules={{ required: true, userName: true }}
          >
            <Input
              value={formValue.Name}
              onChangeValue={value => {
                setFormValue({ ...formValue, Name: value })
              }}
            />
          </FormItem>
          <FormItem
            className="form_item_instance"
            label={_$('关联资源')}
            value={formValue.InstanceIDs}
            rules={{ required: true }}
          >
            <Select
              value={formValue.InstanceIDs}
              clearable
              filterable
              multiple
              container={() => document.getElementsByClassName('form_item_instance')[0]}
              onChangeValue={value => {
                setFormValue({ ...formValue, InstanceIDs: value })
              }}
            >
              {instances?.map(item => (
                <Option key={item.id} value={item.id}>
                  {item.name}
                </Option>
              ))}
            </Select>
          </FormItem>
          <FormItem label={_$('描述')} value={formValue.Description} rules={{ maxLength: 512 }}>
            <Input
              type="textarea"
              value={formValue.Description}
              rows={5}
              resize="none"
              onChangeValue={value => {
                setFormValue({ ...formValue, Description: value })
              }}
            />
          </FormItem>
          <FormItem
            slotLabel={
              <>
                {_$('源告警')}
                <Tooltip content={_$('源告警的标签匹配规则')}>
                  <Icon className="icon ion-help-circled" />
                </Tooltip>
              </>
            }
            value={formValue.SourceParallelMatchers}
            rules={{ required: true }}
          >
            <MatchRule
              rules={formValue.SourceParallelMatchers}
              onChange={value => {
                setFormValue({ ...formValue, SourceParallelMatchers: value })
              }}
            />
          </FormItem>
          <FormItem
            slotLabel={
              <>
                {_$('目标告警')}
                <Tooltip content={_$('目标告警的标签匹配规则')}>
                  <Icon className="icon ion-help-circled" />
                </Tooltip>
              </>
            }
            value={formValue.TargetParallelMatchers}
            rules={{ required: true }}
          >
            <MatchRule
              rules={formValue.TargetParallelMatchers}
              onChange={value => {
                setFormValue({ ...formValue, TargetParallelMatchers: value })
              }}
            />
          </FormItem>
          {/* 验证最多20个匹配条件 */}
          <FormItem
            slotLabel={
              <>
                {_$('匹配条件')}
                <Tooltip content={_$('用于匹配源告警和目标告警的一组标签名')}>
                  <Icon className="icon ion-help-circled" />
                </Tooltip>
              </>
            }
            value={formValue.Equals?.join('\n')}
            rules={{
              denoiseEqualsMaxLength: true
            }}
          >
            <Input
              type="textarea"
              value={formValue.Equals?.join('\n')}
              placeholder={_$('请输入匹配条件，多个条件换行分割')}
              rows={5}
              resize="none"
              onChangeValue={value => {
                setFormValue({ ...formValue, Equals: value.split('\n') })
              }}
            />
          </FormItem>
        </Form>
      </Card>
      <div className="bottom_action">
        <Button onClick={() => formSubmit()} type="primary">
          {_$('确认')}
        </Button>
        <Button
          onClick={() => {
            history.goBack()
          }}
          type="default"
        >
          {_$('取消')}
        </Button>
      </div>
    </div>
  )
}

export default styled(DenoiseInhibitionEdit)`
  padding: 0 16px;
  position: relative;
  padding-bottom: 80px;
  .k-card {
    padding: 16px 0;
    .k-card-body {
      height: calc(100vh - 228px);
      overflow-y: auto;
      padding-bottom: 0;
      padding-top: 0;
      padding-right: 0;
      .k-form-label {
        .ion-help-circled {
          margin-left: 4px;
        }
      }
    }
  }
  .bottom_action {
    display: flex;
    justify-content: flex-end;
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 64px;
    padding: 16px 0;
    background: #ffffff;
    padding-right: 16px;
    z-index: 98;
    border-radius: 6px 6px 0 0;
    button {
      font-size: 14px;
      margin-left: 8px;
    }
  }
`
